import { useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Stack, Tooltip, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { ApiError } from '@cmms/api-client'
import type { Invitation } from '@cmms/types'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useOrgSettings } from '../../../hooks/useOrgSettings'

/// Pending invitations the org admin has sent. Used to recover from
/// failed email delivery — without this the row stays "pending" and
/// `createInvitation` refuses to let the admin invite the same email
/// again. Resend regenerates the token; revoke clears the block.
export function InvitationsList() {
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.invitations.list(),
  })

  // Hide accepted/revoked/expired — the admin only cares about
  // outstanding ones. The full audit lives in the audit log if needed.
  const items: Invitation[] = (list.data ?? []).filter((i) => i.status === 'pending')

  const [confirmFor, setConfirmFor] = useState<Invitation | null>(null)

  const resend = useMutation({
    mutationFn: (id: string) => api.invitations.resend(id),
    onSuccess: () => {
      toast.success('Invitation re-sent')
      qc.invalidateQueries({ queryKey: ['invitations'] })
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'Failed to resend invitation'),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.invitations.revoke(id),
    onSuccess: () => {
      toast.success('Invitation revoked — you can re-invite this email now')
      setConfirmFor(null)
      qc.invalidateQueries({ queryKey: ['invitations'] })
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'Failed to revoke invitation'),
  })

  if (list.isLoading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (items.length === 0) {
    return (
      <Box sx={{ flex: 1, py: 8, textAlign: 'center' }}>
        <Box sx={{
          display: 'inline-flex', width: 56, height: 56,
          borderRadius: 99, bgcolor: 'action.hover',
          alignItems: 'center', justifyContent: 'center', mb: 2,
        }}>
          <MailOutlineIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          No pending invitations
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
          People you invite show up here until they accept.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 2 }}>
      <Stack spacing={1}>
        {items.map((inv) => (
          <Row
            key={inv.id}
            inv={inv}
            onResend={() => resend.mutate(inv.id)}
            onRevoke={() => setConfirmFor(inv)}
            resending={resend.isPending && resend.variables === inv.id}
            revoking={revoke.isPending && revoke.variables === inv.id}
          />
        ))}
      </Stack>

      <Dialog open={!!confirmFor} onClose={() => setConfirmFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Revoke this invitation?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5 }}>
            <strong>{confirmFor?.email}</strong> won't be able to use the
            invite link any more. You'll be able to invite this email again
            after revoking.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmFor(null)}>Cancel</Button>
          <Button
            onClick={() => confirmFor && revoke.mutate(confirmFor.id)}
            disabled={revoke.isPending}
            color="error" variant="contained"
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function Row({
  inv, onResend, onRevoke, resending, revoking,
}: {
  inv: Invitation
  onResend: () => void
  onRevoke: () => void
  resending: boolean
  revoking: boolean
}) {
  const { formatDate } = useOrgSettings()
  const initials = (inv.full_name ?? inv.email).slice(0, 2).toUpperCase()
  const sentAt = formatDate(inv.created_at, true)
  const expiresMs = new Date(inv.expires_at).getTime() - Date.now()
  const expired = expiresMs <= 0
  const hoursLeft = Math.floor(expiresMs / 3_600_000)

  return (
    <Box sx={{
      p: 2, borderRadius: 2, border: '1px solid',
      borderColor: 'divider', bgcolor: 'background.paper',
      display: 'flex', alignItems: 'center', gap: 2,
    }}>
      <Avatar sx={{
        bgcolor: 'primary.light', color: 'primary.main',
        fontSize: 13, fontWeight: 700, width: 38, height: 38,
      }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography sx={{
            fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em',
          }} noWrap>
            {inv.full_name ?? inv.email}
          </Typography>
          {inv.role_name && (
            <Chip size="small" label={inv.role_name}
              sx={{ fontSize: 11, fontWeight: 600, height: 20,
                bgcolor: 'action.hover', color: 'text.secondary' }} />
          )}
          {expired ? (
            <Chip size="small" color="warning" variant="outlined"
              label="Link expired" sx={{ fontSize: 11, fontWeight: 700, height: 20 }} />
          ) : (
            <Chip size="small" variant="outlined"
              label={hoursLeft >= 1 ? `${hoursLeft}h left` : '<1h left'}
              sx={{ fontSize: 11, fontWeight: 600, height: 20 }} />
          )}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
          {inv.full_name ? `${inv.email} · ` : ''}sent {sentAt}
          {inv.invited_by_name ? ` by ${inv.invited_by_name}` : ''}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1}>
        <Tooltip title={expired
          ? 'Issue a fresh 72-hour link and email it again'
          : 'Re-send the existing invite email (refreshes the link)'}>
          <span>
            <Button
              onClick={onResend}
              disabled={resending || revoking}
              startIcon={resending
                ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                : <ForwardToInboxOutlinedIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              sx={{
                textTransform: 'none', borderRadius: 999, px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              {resending ? 'Sending…' : 'Resend'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Cancel this invitation so the email can be re-invited">
          <span>
            <Button
              onClick={onRevoke}
              disabled={resending || revoking}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              variant="text" color="error"
              sx={{
                textTransform: 'none', whiteSpace: 'nowrap',
              }}
            >
              Revoke
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  )
}
